import { inject, Injectable } from '@angular/core';
import { TreatmentApiClient } from 'app/core/api-clients';
import { InProgressTreatment } from 'app/core/models';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments';
import { OrderStatusId } from '../enums';

type SampleOrderSimulationTarget = Pick<InProgressTreatment, 'id' | 'conversation' | 'statusId'>;

@Injectable({
  providedIn: 'root',
})
export class SampleOrderDesignSimulationService {
  private readonly _treatmentClient = inject(TreatmentApiClient);
  private readonly _completedOrderIds = new Set<string>();
  private readonly _runningOrderIds = new Set<string>();

  canSimulate(order?: SampleOrderSimulationTarget | null): boolean {
    return (
      environment.sampleOrderDesignSimulation.enabled &&
      order != null &&
      order.conversation != null &&
      order.statusId != OrderStatusId.printing &&
      order.statusId != OrderStatusId.finished &&
      order.statusId != OrderStatusId.needsApproval &&
      environment.sampleOrderDesignSimulation.designFiles.length > 0 &&
      !this._completedOrderIds.has(order.id) &&
      !this._runningOrderIds.has(order.id)
    );
  }

  async simulate(order: SampleOrderSimulationTarget): Promise<boolean> {
    if (!this.canSimulate(order)) {
      return false;
    }

    const conversationId = order.conversation?.id;
    if (!conversationId) {
      return false;
    }

    this._runningOrderIds.add(order.id);

    try {
      await firstValueFrom(
        this._treatmentClient.triggerSampleOrderDesignSimulation(order.id, {
          message: environment.sampleOrderDesignSimulation.message,
          mockDesigner: {
            userId: environment.sampleOrderDesignSimulation.mockDesigner.userId,
            name: environment.sampleOrderDesignSimulation.mockDesigner.name,
            email: environment.sampleOrderDesignSimulation.mockDesigner.email ?? null,
          },
          designFiles: environment.sampleOrderDesignSimulation.designFiles.map(file => ({
            scanType: file.scanType,
            fileGuid: file.fileGuid,
            filename: file.filename,
            fileSize: file.fileSize,
            fileType: file.fileType,
            subGroup: file.subGroup ?? null,
            teeth: file.teeth ?? null,
          })),
        })
      );

      const didSimulate = true;
      if (didSimulate) {
        this._completedOrderIds.add(order.id);
      }

      return didSimulate;
    } catch (error) {
      console.warn('Failed to simulate sample order design response.', error);
      return false;
    } finally {
      this._runningOrderIds.delete(order.id);
    }
  }
}
